import { type KeyboardEvent, type MutableRefObject, type ReactNode, type RefObject, useEffect, useId, useRef } from 'react'

import { astraTwMerge } from '../Utilities/astraTwMerge'
import { DROPDOWN_PANEL_PADDING_CLASS, DROPDOWN_SEARCH_INPUT_CLASS, DROPDOWN_SEARCH_LIST_CLASS, DROPDOWN_SEARCH_ROW_CLASS } from './dropdownStyles'

export type DropdownMenuSearch = {
    /**
     * The current query. The field is controlled, and narrowing the list is the consumer's job: only
     * they know what an option matches on — a surgeon's full name behind their initials, a count that
     * should survive filtering — and whether the answer comes from a server.
     */
    value: string
    onChange: (value: string) => void
    /**
     * Placeholder text, which also names the field for a screen reader unless `label` says otherwise.
     */
    placeholder?: string
    /**
     * An accessible name for the field, for when the placeholder is not one.
     */
    label?: string
}

/**
 * The state a searching panel navigates with. The caret never leaves the field, so an item is marked
 * active rather than focused, and the arrow keys and Enter move and use that mark.
 */
export type DropdownSearchNavigation = {
    itemsRef: MutableRefObject<Array<HTMLElement | null>>
    activeIndex: number | null
    setActiveIndex: (index: number | null) => void
}

/** Keys the field hands to the menu: Esc dismisses it and Tab leaves it. Everything else it handles itself. */
const MENU_KEYS = new Set(['Escape', 'Tab'])

const isSelectable = (item: HTMLElement | null): item is HTMLElement => item !== null && item.getAttribute('aria-disabled') !== 'true'

type SearchFieldProps = {
    search: DropdownMenuSearch
    ref: RefObject<HTMLInputElement | null>
    navigation: DropdownSearchNavigation
    itemIdPrefix: string
    listId: string
}

const DropdownSearchField = ({ search, ref, navigation, itemIdPrefix, listId }: SearchFieldProps) => {
    const { itemsRef, activeIndex, setActiveIndex } = navigation

    // Narrowing the list rebuilds it, and the first row of whatever is left takes the mark: Enter should
    // mean the obvious result, not whatever happened to sit at that position before the last keystroke.
    useEffect(() => {
        setActiveIndex(0)
    }, [search.value, setActiveIndex])

    const step = (direction: 1 | -1) => {
        const items = itemsRef.current
        const from = activeIndex ?? (direction === 1 ? -1 : items.length)

        for (let index = from + direction; index >= 0 && index < items.length; index += direction) {
            if (isSelectable(items[index] ?? null)) return setActiveIndex(index)
        }
        // Nothing further in that direction, so the mark stays where it is rather than wrapping: a
        // filtered list is short, and jumping from its end to its start reads as a jump to nowhere.
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (MENU_KEYS.has(event.key)) return

        // ArrowLeft is the caret's while there is text to its left, and the menu's once there is not:
        // in a submenu it closes the panel, which is the only way back out by keyboard.
        const caretAtStart = event.currentTarget.selectionStart === 0 && event.currentTarget.selectionEnd === 0
        if (event.key === 'ArrowLeft' && caretAtStart) return

        // Every other key is the field's: typing must reach the input rather than the menu's typeahead,
        // and ArrowLeft has to move the caret rather than close the submenu the field sits in.
        event.stopPropagation()

        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            step(event.key === 'ArrowDown' ? 1 : -1)
        }

        if (event.key === 'Enter' && activeIndex !== null) {
            event.preventDefault()
            const active = itemsRef.current[activeIndex]
            if (isSelectable(active ?? null)) active?.click()
        }
    }

    return (
        <div className={DROPDOWN_SEARCH_ROW_CLASS}>
            <input
                ref={ref}
                type="text"
                value={search.value}
                onChange={event => search.onChange(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={search.placeholder ?? 'Søk...'}
                aria-label={search.label ?? search.placeholder ?? 'Søk'}
                aria-controls={listId}
                aria-activedescendant={activeIndex === null ? undefined : `${itemIdPrefix}-${activeIndex}`}
                autoComplete="off"
                spellCheck={false}
                className={DROPDOWN_SEARCH_INPUT_CLASS}
            />
        </div>
    )
}

type Props = {
    search: DropdownMenuSearch | undefined
    searchRef: RefObject<HTMLInputElement | null>
    navigation: DropdownSearchNavigation
    itemIdPrefix: string
    noPadding?: boolean
    children: ReactNode
}

/**
 * A panel's body. Without a search field it is the items themselves, so a menu that does not search
 * keeps the markup it had. With one, the field is pinned above a list that scrolls under it.
 *
 * The list then carries the `menu` role rather than the panel: a menu may only own menu items, and a
 * text field is not one. The panel around the two becomes a dialog.
 */
export const DropdownPanelBody = ({ search, searchRef, navigation, itemIdPrefix, noPadding, children }: Props) => {
    const listId = useId()
    const listRef = useRef<HTMLDivElement>(null)

    // Choosing a row puts the browser's focus on it. In a panel that searches, the caret belongs in the
    // field: a checkbox list is worked through by typing, choosing, and typing again.
    useEffect(() => {
        const list = listRef.current
        if (!list) return

        const returnCaretToField = () => searchRef.current?.focus()
        list.addEventListener('click', returnCaretToField)
        return () => list.removeEventListener('click', returnCaretToField)
    }, [searchRef])

    if (!search) return children

    return (
        <>
            <DropdownSearchField search={search} ref={searchRef} navigation={navigation} itemIdPrefix={itemIdPrefix} listId={listId} />
            <div id={listId} role="menu" ref={listRef} className={astraTwMerge(DROPDOWN_SEARCH_LIST_CLASS, noPadding ? '' : DROPDOWN_PANEL_PADDING_CLASS)}>
                {children}
            </div>
        </>
    )
}
